import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { MenuItem } from '@material-ui/core';
import { TextField, Button } from '@avangard/ui/core';

import { useEnqueueStacks } from '@modules/layout/hooks';
import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { schoolTypeTranslates } from '@modules/schools/constants';
import { CreateSchoolRequest } from '@modules/schools/requests';
import { CreateSchoolMutation } from '@modules/schools/graphql';
import { SchoolType } from '@modules/types/graphql';
import { SchoolCreateFormLocationGroup } from './school-create-form-location-group';

import type { PureQueryOptions } from '@apollo/client';
import type { CreateSchoolFormValues } from '@modules/schools/requests';
import type {
    CreateSchoolMutationType,
    CreateSchoolMutationVariables,
} from '@modules/types/graphql';

export type SchoolCreateFormProps = {
    refetchQueries: PureQueryOptions[];
    onCancel: () => void;
    onSuccess: (schoolId: string) => void;
};

const StyledForm = styled(Form)`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const schoolTypes = Object.values(SchoolType);

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const SchoolCreateForm = (props: SchoolCreateFormProps) => {
    const { refetchQueries, onCancel, onSuccess } = props;

    const { t } = useTranslation(['common', 'errors', 'schools']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const [createSchool] = useMutation<CreateSchoolMutationType, CreateSchoolMutationVariables>(
        CreateSchoolMutation,
    );

    return (
        <ExtendedFormik<CreateSchoolFormValues>
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={yup.object({
                location: yup.mixed().required(t('errors:validations.required') ?? ''),
                type: yup.string().required(t('errors:validations.required') ?? ''),
                shortName: yup.string().required(t('errors:validations.required') ?? ''),
                email: yup.string().required(t('errors:validations.required') ?? ''),
            })}
            initialValues={{
                region: null,
                location: null,
                type: '',
                shortName: '',
                number: '',
                email: '',
            }}
            onSubmit={async values => {
                try {
                    const item = new CreateSchoolRequest(values);

                    const { data: createSchoolData } = await createSchool({
                        variables: { item },
                        refetchQueries,
                        awaitRefetchQueries: true,
                    });

                    const schoolId = createSchoolData?.createSchool?.id;

                    if (schoolId) {
                        enqueueSuccess(t('schools:notifiers.add_school.success'));
                        onSuccess(schoolId);
                    } else {
                        enqueueError(t('schools:notifiers.add_school.error'));
                    }
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { values, errors, handleChange, handleBlur, setFieldValue, isSubmitting } =
                    formikProps;

                const handleChangeSelect = (
                    event: React.ChangeEvent<{ name: string; value: string }>,
                ) => setFieldValue(event.target.name, event.target.value);

                return (
                    <StyledForm>
                        <SchoolCreateFormLocationGroup />

                        <TextField
                            select
                            required
                            fullWidth
                            id='type'
                            name='type'
                            label={t('common:forms.labels.school_type')}
                            value={values.type}
                            error={!!errors.type}
                            helperText={errors.type}
                            onChange={handleChangeSelect}
                            onBlur={handleBlur}
                        >
                            <MenuItem value=''>
                                {t('common:forms.placeholder.choose_school_type_long')}
                            </MenuItem>

                            {schoolTypes.map(schoolType => (
                                <MenuItem key={schoolType} value={schoolType}>
                                    {t(schoolTypeTranslates[schoolType])}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            required
                            fullWidth
                            id='shortName'
                            label={t('common:forms.labels.school_short_name')}
                            placeholder={
                                t('common:forms.placeholder.typing_school_short_name') ?? ''
                            }
                            value={values.shortName}
                            error={!!errors.shortName}
                            helperText={errors.shortName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            fullWidth
                            id='number'
                            label={t('common:forms.labels.school_number')}
                            placeholder={t('common:forms.placeholder.typing_school_number') ?? ''}
                            value={values.number}
                            error={!!errors.number}
                            helperText={errors.number}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            required
                            fullWidth
                            id='email'
                            label={t('common:forms.labels.email')}
                            placeholder={t('common:forms.placeholder.typing_email') ?? ''}
                            value={values.email}
                            error={!!errors.email}
                            helperText={errors.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <FormRowSubmit>
                            <Button variant='outlined' disabled={isSubmitting} onClick={onCancel}>
                                {t('common:actions.cancel')}
                            </Button>

                            <Button type='submit' loading={isSubmitting}>
                                {t('common:actions.add')}
                            </Button>
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { SchoolCreateForm };
